import withStyles from "@material-ui/core/styles/withStyles"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { fetchUserAlerts } from "../../actions/alertActions"
import { fetchDevice } from "../../actions/deviceActions"
import { fetchGroups } from "../../actions/groupActions"
import { fetchUserInsights } from "../../actions/userInsightsActions"
import { USER_ALERT_KEYS, USER_ALERT_STATUS } from "../../util/constants"
import { getDisplayName } from "../../util/deviceHelpers"
import { useLazyLoadSelector } from "../../util/redux"
import CenteredCircularProgress from "../Common/CenteredCircularProgress"
import MapView from "../Map/MapView"

const styles = () => ({
  center: {
    width: "100%",
    height: "100%",
    textAlign: "center",
  },
  centerLoading: {
    minHeight: "80vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
})

const MapPage = (props) => {
  const { classes, setTab } = props
  const [points, setPoints] = useState([])
  const devices = useLazyLoadSelector((state) => state.device.deviceData, fetchDevice, [], false)
  const insights = useLazyLoadSelector(
    (state) => state.userInsights.userInsightsData,
    fetchUserInsights,
    [],
    false,
  )
  const alerts = useLazyLoadSelector((state) => state.alerts.alerts, fetchUserAlerts, [], false)
  const groups = useLazyLoadSelector((state) => state.group.groupData, fetchGroups, [], false)
  const loadingInsights = useSelector((state) => state.userInsights.userInsightInfoLoading || false)
  const loadingDevices = useSelector((state) => state.device.loadingDevices || false)
  const loadingAlerts = useSelector((state) => state.alerts.loadingAlerts || false)

  useEffect(() => {
    if (devices && insights && alerts && groups) {
      const data = insights

      const insightMetrics = {}
      const alertMap = {}
      const groupMap = {}

      groups.forEach((group) => {
        for (const device of group.devices) {
          const id = device.toString()
          if (!Object.keys(groupMap).includes(id)) {
            groupMap[id] = new Set()
          }
          groupMap[id].add(group.group_name)
        }
      })

      data.forEach((insight) => {
        const tempTimestamp = new Date(insight.time_stamp).getTime()
        const tempOnDeviceTimestamp = new Date(insight.file_ctime).getTime()
        if (!insightMetrics[insight.device_id]) {
          insightMetrics[insight.device_id] = {
            count: 0,
            mostRecentInsight: insight,
            mostRecentOnDeviceInsight: insight,
            mostRecentInsightTimestamp: tempTimestamp,
            mostRecentOnDeviceInsightTimestamp: tempOnDeviceTimestamp,
          }
        }
        insightMetrics[insight.device_id].count += 1
        if (tempTimestamp > insightMetrics[insight.device_id].mostRecentInsightTimestamp) {
          insightMetrics[insight.device_id].mostRecentInsightTimestamp = tempTimestamp
          insightMetrics[insight.device_id].mostRecentInsight = insight
        }

        if (
          tempOnDeviceTimestamp >
          insightMetrics[insight.device_id].mostRecentOnDeviceInsightTimestamp
        ) {
          insightMetrics[insight.device_id].mostRecentOnDeviceInsightTimestamp =
            tempOnDeviceTimestamp
          insightMetrics[insight.device_id].mostRecentOnDeviceInsight = insight
        }
      })

      alerts
        .filter((alert) => alert.alert_key === USER_ALERT_KEYS.DEVICE_ID)
        .filter(
          (alert) =>
            alert.status === USER_ALERT_STATUS.NEW ||
            alert.status === USER_ALERT_STATUS.ACKNOWLEDGED,
        )
        .forEach((alert) => {
          const id = alert.associated_id.toString()
          if (!Object.keys(alertMap).includes(id)) {
            alertMap[id] = new Set()
          }
          alertMap[id].add(alert.alert_type)
        })

      const newPoints = devices
        .filter((d) => d?.latitude && d.longitude)
        .filter(
          (d) => d.latitude >= -90 && d.latitude <= 90 && d.longitude >= -180 && d.longitude <= 180,
        )
        .map((d) => {
          return {
            latitude: d.latitude,
            longitude: d.longitude,
            device: d,
            insightMetrics: insightMetrics[d.device_id],
            alerts: alertMap[d.device_id] || new Set(),
            groups: d.device_id.toString() in groupMap ? [...groupMap[d.device_id]] : [],
            device_name: getDisplayName(d),
            device_id: d.device_id,
            device_battery: d.battery_voltage,
            alert_type:
              d.device_id.toString() in alertMap ? [...alertMap[d.device_id]].join(", ") : "",
            last_seen: d.last_seen,
            location_name: d.location_name,
          }
        })
      setPoints(newPoints)
    }
  }, [devices, insights, alerts, groups])

  return loadingDevices || loadingInsights || loadingAlerts ? (
    <div className={classes.centerLoading}>
      <CenteredCircularProgress size={150} />
    </div>
  ) : points.length > 0 ? (
    <MapView points={points} setTab={setTab} />
  ) : (
    <h1 className={classes.center}>No Devices Available</h1>
  )
}

export default withStyles(styles)(MapPage)
